import { useParams, useNavigate, NavLink } from "react-router-dom";
import { loadingActive, pageLoadingActive } from "../../components/Background";
import {
  ChatViewer,
  MessageWriteComponent,
} from "../../components/MessageComponents";
import { useState, useEffect, useContext, useRef } from "react";
import { formatTimestamp, setImageUrl } from "../../utils";
import { MdDone } from "react-icons/md";
import {
  BanUserButton,
  CategoryBubble,
  PriorityBubble,
  SideActionButton,
  StatusBubble,
  TicketAssignModal,
  TicketAttrEditRow,
  TicketAttrRow,
  TicketPutOnHoldModal,
  TicketReopenModal,
  TicketRetainAttachmentsModal,
  TicketSubmitAndCloseWarningModal,
  TicketSubmitAndResolveWarningModal,
} from "../../components/TicketComponents";
import { AccountContext } from "../../contexts/AccountContexts";
import { NoteViewer } from "../../components/NotesComponents";
import ImageViewer from "../../components/ImageComponents";
import { CiEdit } from "react-icons/ci";
import { IoCloseSharp, IoShieldSharp } from "react-icons/io5";
import { IoIosPause, IoIosPerson } from "react-icons/io";
import {
  MdDoneOutline,
  MdOutlineSentimentVeryDissatisfied,
  MdNoteAlt,
} from "react-icons/md";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";

import {
  sendMessage,
  sendMessageWithFileLinks,
  uploadFiles,
} from "../../utils/messagingUtils";
import { NotificationContext } from "../../contexts/NotificationContexts";
import { ContentContext } from "../../contexts/ContentContext";
import {
  requestEditTicket,
  requestFetchTicketsLast5,
  requestSubmitCompleteTicket,
  requestTicket,
} from "../../requests/ticket_requests";
import { TimePassedComponent } from "../../components/TimeComponents";
import { PageHeader } from "../../components/HeaderComponents";
import { useTranslation } from "react-i18next";
import { BreadCrumbsGeneral } from "../../components/NavigationComponents";
import { BanHistory, BanModal } from "../../components/BanComponents";
import { requestNewChatMessage } from "../../requests/chat_requests";
import { checkDontShowAgain } from "../../components/ModalComponents";
import { MemberProfileComponent } from "../../components/DeveloperComponents";
import { FiShield, FiShieldOff } from "react-icons/fi";
import { VscIssueReopened } from "react-icons/vsc";

const EditTicket = () => {
  const { t } = useTranslation("edit");
  const { ticket_info } = useParams();
  const currentTicketInfoRef = useRef(ticket_info);
  currentTicketInfoRef.current = ticket_info;

  const { user, devs, userPreferences, language } = useContext(AccountContext);
  const { socket, isSidebar, setIsSidebar } = useContext(NotificationContext);
  const { categories, priorities, servers, deletedServers, openErrorModal } =
    useContext(ContentContext);
  const [categoryOptions, setCategoryOptions] = useState({});

  const [ticketInfo, setTicketInfo] = useState(null);

  const [completeAction, setCompleteAction] = useState(false);
  const autoRedirect = userPreferences
    ? userPreferences.redirect_after_ticket_complete
    : false;

  // Ticket edit variables
  const [editedCategory, setEditedCategory] = useState(null);
  const [editedPriority, setEditedPriority] = useState(null);

  const [editWindowOpen, setEditWindowOpen] = useState(false);

  const [newMessage, setNewMessage] = useState("");
  const [newFiles, setNewFiles] = useState([]);

  const [anonUser, setAnonUser] = useState(null);

  const [initLoading, setInitLoading] = useState(true);

  // The reopen ticket function either through reply of fast action
  const [isByReply, setIsByReply] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [lastTickets, setLastTickets] = useState([]);
  const [showMobileLastTickets, setShowMobileLastTickets] = useState(false);
  const [showMobileNotes, setShowMobileNotes] = useState(false);

  const navigator = useNavigate();

  useEffect(() => {
    if (isSidebar) {
      setIsSidebar(false);
    }

    socket.off("ticket_assigned");
    socket.on("ticket_assigned", (pckg) => {
      setTicketInformation({ ticketData: pckg.ticket });
    });
  }, []);

  const setNotesId = (notesId) => {
    setTicketInfo({
      ...ticketInfo,
      notes_id: notesId,
    });
  };

  const setTicketInformation = ({ ticketData, userData }) => {
    setTicketInfo(ticketData);
    if (userData) {
      setAnonUser(userData);
    }
  };

  useEffect(() => {
    if (completeAction && autoRedirect) {
      setTimeout(() => {
        navigator("/admin/ticket");
      }, 100);
      setCompleteAction(false);
    }
  }, [completeAction]);

  useEffect(() => {
    let temp = categories;
    Object.values(categories).forEach((c) => {
      temp[c.id].title = c?.locales?.[language]
        ? c?.locales?.[language]
        : c.title;
    });
    setCategoryOptions((prev) => ({ ...temp }));
  }, [categories]);

  useEffect(() => {
    // Upon opening the page decode the ticket_info and make a ticket info request

    pageLoadingActive(true);
    const decoded = JSON.parse(atob(ticket_info));
    requestTicket({
      ticketId: decoded.ticketId,
      ticketEmail: decoded.email,
      ticketNumber: decoded?.ticketNumber,
    })
      .then((data) => {
        try {
          const currentDecoded = JSON.parse(atob(currentTicketInfoRef.current));
          if (String(currentDecoded.ticketId) === String(data.ticket.id)) {
            setTicketInformation({ ticketData: data.ticket });
            setAnonUser(data.user);
            socket.emit("user_ticket_visit", { ticket_id: decoded.ticketId });
          }
        } catch (e) {
          console.error(e);
        }
        pageLoadingActive(false);
      })
      .catch((error) => {
        console.error(error.status, error.message);
        openErrorModal(error);
      })
      .finally(() => {
        setIsSendingMessage(false);
      });
  }, [ticket_info]);

  const handleSendMessage = (message, files) => {
    setIsSendingMessage(true);
    setNewMessage(message);
    setNewFiles(files);
    if (ticketInfo.status === "RESOLVED" || ticketInfo.status === "CLOSED") {
      setIsByReply(true);
      document.getElementById("ticket-reopen-modal").showModal();
      setIsSendingMessage(false);
      return false;
    } else {
      if (files.length > 0) {
        sendMessageWithFileLinks({
          socket: socket,
          message: message,
          files: files,
          ticketInfo: ticketInfo,
          user: user,
          replyTo: replyTo?.type === "message" ? replyTo : null,
        }).then(() => {
          setIsSendingMessage(false);
          replyTo?.type === "message" && setReplyTo(null);
        });
      } else {
        sendMessage({
          socket: socket,
          message: message,
          ticketInfo: ticketInfo,
          user: user,
          replyTo: replyTo?.type === "message" ? replyTo : null,
        }).then(() => {
          setIsSendingMessage(false);
          replyTo?.type === "message" && setReplyTo(null);
        });
      }
      return true;
    }
  };

  const editSubmitDisabledCondition =
    ticketInfo &&
    editedCategory === ticketInfo.category &&
    editedPriority === ticketInfo.priority;

  const onSubmitEdit = () => {
    pageLoadingActive(true);
    requestEditTicket({
      ticketId: ticketInfo.id,
      editedCategory: editedCategory,
      editedPriority,
      editedPriority,
    }).then((data) => {
      try {
        const currentDecoded = JSON.parse(atob(currentTicketInfoRef.current));
        if (String(currentDecoded.ticketId) === String(data.ticket.id)) {
          setTicketInformation({ ticketData: data.ticket });
        }
      } catch (e) {
        console.error(e);
      }
      pageLoadingActive(false);
      onCloseEdit();
    });
  };

  const onCloseEdit = () => {
    setEditWindowOpen(false);
    setEditedCategory(null);
    setEditedPriority(null);
  };

  const getData = (message, files) => {
    setNewMessage(message);
    setNewFiles(files);
  };

  const [completeInProgress, setCompleteInProgress] = useState(false);

  const handleSendAndResolve = async (msg, fls) => {
    loadingActive(true);
    let fileUrls = [];
    if (fls && fls.length > 0) {
      const fileUploadResponse = await uploadFiles(fls);
      fileUrls = fileUploadResponse.data.fileUrls;
    } else if (newFiles.length > 0) {
      const fileUploadResponse = await uploadFiles(newFiles);
      fileUrls = fileUploadResponse.data.fileUrls;
    }
    setCompleteInProgress(true);
    requestNewChatMessage({
      chatId: ticketInfo.chat_id,
      user_class: user.class,
      message: msg ?? newMessage,
      sender_id: user.id,
      sender_name: user.name,
      ticket_id: ticketInfo.id,
      attachments: fileUrls,
      replyTo: replyTo,
    })
      .then(() => {
        requestSubmitCompleteTicket({
          ticketInfo: ticketInfo,
          action: "RESOLVED",
          excuse: "",
          isUnAnswered: false,
        })
          .then((data) => {
            try {
              const currentDecoded = JSON.parse(
                atob(currentTicketInfoRef.current),
              );
              if (
                String(currentDecoded.ticketId) === String(data.ticket_data.id)
              ) {
                setTicketInformation({ ticketData: data.ticket_data });
              }
            } catch (e) {
              console.error(e);
            }
            document
              .getElementById("ticket-submit-and-resolve-warning-modal")
              .close();
            setCompleteAction(true);
            setReplyTo(null);
          })
          .catch((error) => {
            console.error(error.status, error.message);
            openErrorModal(error);
          })
          .finally(() => {
            setCompleteInProgress(false);
            loadingActive(false);
            setNewMessage("");
            setNewFiles([]);
            setIsSendingMessage(false);
          });
      })
      .catch((error) => {
        console.error(error.status, error.message);
        openErrorModal(error);
      });
  };

  const handleSendAndClose = async (msg, fls) => {
    loadingActive(true);
    let fileUrls = [];
    if (fls && fls.length > 0) {
      const fileUploadResponse = await uploadFiles(fls);
      fileUrls = fileUploadResponse.data.fileUrls;
    } else if (newFiles.length > 0) {
      const fileUploadResponse = await uploadFiles(newFiles);
      fileUrls = fileUploadResponse.data.fileUrls;
    }
    setCompleteInProgress(true);
    requestNewChatMessage({
      chatId: ticketInfo.chat_id,
      user_class: user.class,
      message: msg ?? newMessage,
      sender_id: user.id,
      sender_name: user.name,
      ticket_id: ticketInfo.id,
      attachments: fileUrls,
      replyTo: replyTo,
    })
      .then(() => {
        requestSubmitCompleteTicket({
          ticketInfo: ticketInfo,
          action: "CLOSED",
          excuse: "",
          isUnAnswered: false,
        })
          .then((data) => {
            try {
              const currentDecoded = JSON.parse(
                atob(currentTicketInfoRef.current),
              );
              if (
                String(currentDecoded.ticketId) === String(data.ticket_data.id)
              ) {
                setTicketInformation({ ticketData: data.ticket_data });
              }
            } catch (e) {
              console.error(e);
            }
            document
              .getElementById("ticket-submit-and-close-warning-modal")
              .close();
            setCompleteAction(true);
            setReplyTo(null);
          })
          .catch((error) => {
            console.error(error.status, error.message);
            openErrorModal(error);
          })
          .finally(() => {
            setCompleteInProgress(false);
            loadingActive(false);
            setNewMessage("");
            setNewFiles([]);
            setIsSendingMessage(false);
          });
      })
      .catch((error) => {
        console.error(error.status, error.message);
        openErrorModal(error);
      });
  };

  const handleReopen = () => {
    pageLoadingActive(true);
    if (newFiles.length > 0) {
      sendMessageWithFileLinks({
        socket: socket,
        message: newMessage,
        files: newFiles,
        ticketInfo: ticketInfo,
        user: user,
        replyTo: replyTo?.type === "message" ? replyTo : null,
      });
    } else {
      sendMessage({
        socket: socket,
        message: newMessage,
        ticketInfo: ticketInfo,
        user: user,
        replyTo: replyTo,
      });
    }
    setNewMessage("#FINISHED#");
    setNewFiles([]);
    pageLoadingActive(false);
    setReplyTo(null);
  };

  const handleSendAndComplete = (resolved, message, files) => {
    if (ticketInfo.status === "CLOSED" || ticketInfo.status === "RESOLVED") {
      return;
    }
    setNewMessage(message);
    setNewFiles(files);
    if (resolved) {
      const isDontShow = checkDontShowAgain(
        user.id,
        "ticket-resolve-warning-modal",
      );
      if (isDontShow) {
        handleSendAndResolve(message, files);
      } else {
        document
          .getElementById("ticket-submit-and-resolve-warning-modal")
          .showModal();
      }
    } else {
      const isDontShow = checkDontShowAgain(
        user.id,
        "ticket-close-warning-modal",
      );
      if (isDontShow) {
        handleSendAndClose(message, files);
      } else {
        document
          .getElementById("ticket-submit-and-close-warning-modal")
          .showModal();
      }
    }
  };

  useEffect(() => {
    if (anonUser?.id) {
      requestFetchTicketsLast5({ userId: anonUser.id })
        .then((data) => {
          setLastTickets(data.tickets);
        })
        .catch((err) => console.error("Geçmiş biletler yüklenemedi", err));
    }
  }, [anonUser?.id]);

  if (anonUser == null || ticketInfo === null) {
    return <></>;
  }

  if (initLoading) {
    setInitLoading(false);
  }

  const breadCrumbs = [
    {
      link: "/admin/dashboard",
      name: t("Home"),
    },
    {
      link: "/admin/ticket",
      name: t("Tickets"),
    },
    {
      link: null,
      name: ticketInfo.createdAt,
    },
  ];

  return (
    <>
      <TicketAssignModal
        socket={socket}
        currentCategory={ticketInfo.category}
        setTicketInformation={setTicketInformation}
        ticketId={ticketInfo.id}
        ticketServer={ticketInfo.server}
        chatId={ticketInfo.chat_id}
        assignedTo={ticketInfo.assignedTo}
      />
      <TicketReopenModal
        ticketInfo={ticketInfo}
        setTicketInformation={setTicketInformation}
        isByReply={isByReply}
        messageFunction={() => {
          handleReopen();
        }}
      />
      <TicketPutOnHoldModal
        ticketInfo={ticketInfo}
        setTicketInformation={setTicketInformation}
      />
      <TicketSubmitAndResolveWarningModal
        completeInProgress={completeInProgress}
        ticketInfo={ticketInfo}
        callback={handleSendAndResolve}
      />
      <TicketSubmitAndCloseWarningModal
        newMessage={newMessage}
        ticketInfo={ticketInfo}
        callback={handleSendAndClose}
      />
      <TicketRetainAttachmentsModal
        ticketInfo={ticketInfo}
        setTicketInformation={setTicketInformation}
      />
      <BanModal
        anonUser={anonUser}
        ticketInfo={ticketInfo}
        setTicketInformation={setTicketInformation}
      />

      <main className="flex flex-col justify-start items-center w-full py-2 gap-5 pb-10 px-4 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300">
        <PageHeader pageTitle={`${t("Ticket")} - ${ticketInfo.createdAt}`} />
        <div className=" flex w-full justify-between lg:grid lg:grid-cols-3">
          <div className="flex flex-col flex-1">
            <h1 className="text-xl font-semibold">
              {t("Ticket")} - {ticketInfo.createdAt}
            </h1>
            <BreadCrumbsGeneral elements={breadCrumbs} />
          </div>

          <div className="flex flex-col gap-1 items-center">
            <div className="flex flex-col gap-1 items-center">
              <span className="text-sm">{t("Ticket assigned to")}</span>
              {ticketInfo.assignedTo ? (
                <div className="flex items-center p-1 gap-2 border rounded-lg max-w-48 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600  text-sm px-2">
                  <div className="chat-image avatar">
                    <div className="w-8 rounded-full">
                      {devs[ticketInfo.assignedTo].avatar.length < 3 ? (
                        <div className="w-8 h-8 rounded-full bg-fuchsia-700 flex items-center justify-center text-white text-md select-none">
                          {"GM"}
                        </div>
                      ) : (
                        <img
                          alt="Tailwind CSS chat bubble component"
                          src={setImageUrl(devs[ticketInfo.assignedTo].avatar)}
                        />
                      )}
                    </div>
                  </div>
                  <h1>{devs[ticketInfo.assignedTo]?.name}</h1>
                </div>
              ) : (
                <span className="text-lg text-red-600 dark:text-red-500 font-semibold">
                  {t("Not Assigned")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col xl:grid 2xl:grid-cols-[3fr_2fr] w-full gap-5">
          <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-xl shadow-lg gap-3 py-5">
            <div className="flex w-full flex-wrap lg:grid lg:grid-cols-[1fr_1fr_auto] px-5 gap-5">
              <div className="flex flex-col gap-3">
                <div
                  className={`flex flex-col ${anonUser.banned ? "text-red-600 dark:text-red-500" : ""}`}
                >
                  <div className="font-semibold">
                    <MemberProfileComponent
                      member={
                        anonUser?.rank === "user" &&
                        anonUser?.type !== "temp" &&
                        anonUser
                      }
                      ticket={ticketInfo}
                    />
                  </div>
                  <span className="text-sm font-semibold">
                    {ticketInfo.email}
                  </span>
                </div>
                {anonUser.banned && (
                  <span className="text-red-600 text-lg font-bold">
                    {t("Banned")}
                  </span>
                )}
                {ticketInfo && anonUser && (
                  <BanHistory ticketInfo={ticketInfo} ticketUser={anonUser} />
                )}
                <TicketAttrRow
                  attrName={t("Server")}
                  attrContent={
                    servers[ticketInfo.server]?.title ??
                    deletedServers[ticketInfo.server]?.title ??
                    ticketInfo.server
                  }
                />
                <TicketAttrRow
                  attrName={t("Character Name")}
                  attrContent={ticketInfo.characterName}
                />
                {ticketInfo.violatersName &&
                  ticketInfo.violatersName !== "" && (
                    <div className="text-red-600 dark:text-red-500">
                      <TicketAttrRow
                        attrName={t("Violater's Name")}
                        attrContent={ticketInfo.violatersName}
                      />
                    </div>
                  )}
                <div className="flex flex-col gap-3 mt-5">
                  <TicketAttrRow
                    attrName={t("Created At")}
                    attrContent={formatTimestamp(ticketInfo.createdAt * 1000)}
                  />
                  <div className="flex gap-2 items-center">
                    <TicketAttrRow
                      attrName={t("Last Update")}
                      attrContent={`${formatTimestamp(ticketInfo.lastUpdated * 1000)}`}
                    />
                    <span className="font-semibold">
                      (
                      <TimePassedComponent
                        key={ticketInfo.id}
                        timestamp={ticketInfo.lastUpdated * 1000}
                      />
                      )
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="flex flex-col w-[425px] gap-3 lg:h-full">
                  {editWindowOpen ? (
                    <>
                      <TicketAttrEditRow
                        attrName={t("Category")}
                        selectedAttr={ticketInfo.category}
                        placeHolder={t("Select new category")}
                        options={
                          user.rank === "admin"
                            ? Object.keys(categories)
                            : user.categories
                        }
                        setNew={setEditedCategory}
                        collection={categoryOptions}
                      />
                      <TicketAttrEditRow
                        attrName={t("Priority")}
                        selectedAttr={ticketInfo.priority}
                        placeHolder={t("Select new priority level")}
                        options={Object.keys(priorities)}
                        setNew={setEditedPriority}
                        collection={priorities}
                      />
                      <TicketAttrRow
                        attrName={t("Status")}
                        attrContent={
                          <StatusBubble ticketStatus={ticketInfo.status} />
                        }
                      />
                      <div className="flex w-full justify-center mt-auto gap-2">
                        <SideActionButton
                          key="submit-done-edit-key"
                          content={<MdDone />}
                          callback={onSubmitEdit}
                          disabled={editSubmitDisabledCondition}
                        />
                        <SideActionButton
                          key="edit-close-key"
                          content={<IoCloseSharp />}
                          callback={onCloseEdit}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <TicketAttrRow
                        attrName={t("Category")}
                        attrContent={
                          <CategoryBubble
                            ticketCategory={ticketInfo.category}
                          />
                        }
                      />

                      <TicketAttrRow
                        attrName={t("Priority")}
                        attrContent={
                          <PriorityBubble
                            ticketPriority={ticketInfo.priority}
                          />
                        }
                      />
                      <TicketAttrRow
                        attrName={t("Status")}
                        attrContent={
                          <StatusBubble ticketStatus={ticketInfo.status} />
                        }
                      />
                      {(ticketInfo.status == "RESOLVED" ||
                        ticketInfo.status == "CLOSED") &&
                        ticketInfo.excuse && (
                          <TicketAttrRow
                            attrName={t("Reason")}
                            attrContent={t(ticketInfo.excuse)}
                          />
                        )}
                      {ticketInfo.status == "RESOLVED" &&
                        ticketInfo.resolved_by && (
                          <TicketAttrRow
                            attrName={t("Resolved By")}
                            attrContent={
                              devs[ticketInfo.resolved_by]?.name ??
                              t("User", { ns: "settings" })
                            }
                          />
                        )}
                      {ticketInfo.status == "CLOSED" &&
                        ticketInfo.closed_by && (
                          <TicketAttrRow
                            attrName={t("Closed By")}
                            attrContent={
                              ticketInfo.closed_by === "AUTO"
                                ? t("Auto")
                                : devs[ticketInfo.closed_by]?.name
                            }
                          />
                        )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col px-2 items-end">
                {!editWindowOpen && (
                  <>
                    <div className="flex flex-col gap-2">
                      <SideActionButton
                        key="init-edit-key"
                        title={t("Edit ticket")}
                        disabled={
                          !(
                            ticketInfo.status === "RESOLVED" ||
                            ticketInfo.status === "CLOSED"
                          )
                            ? ticketInfo.assignedTo !== user.id &&
                              user.rank !== "admin"
                            : ticketInfo.closed_by !== user.id &&
                              ticketInfo.resolved_by !== user.id &&
                              user.rank !== "admin"
                        }
                        content={<CiEdit />}
                        callback={() => {
                          setEditWindowOpen(!editWindowOpen);
                        }}
                      />
                      <SideActionButton
                        key="assign-to-another-person-key"
                        title={t("Assign to user")}
                        disabled={
                          ticketInfo.status === "RESOLVED" ||
                          ticketInfo.status === "CLOSED" ||
                          (ticketInfo.assignedTo !== user.id &&
                            user.rank !== "admin")
                        }
                        content={<IoIosPerson />}
                        callback={() =>
                          document
                            .getElementById("ticket-assign-modal")
                            .showModal()
                        }
                      />
                      <SideActionButton
                        key="ticket-put-on-hold-key"
                        title={t("Put ticket on hold")}
                        disabled={
                          ticketInfo.status === "RESOLVED" ||
                          ticketInfo.status === "CLOSED" ||
                          ticketInfo.status === "ON HOLD" ||
                          (ticketInfo.assignedTo !== user.id &&
                            user.rank !== "admin")
                        }
                        content={<IoIosPause />}
                        callback={() =>
                          document
                            .getElementById("ticket-put-on-hold-modal")
                            .showModal()
                        }
                      />
                      <BanUserButton
                        key="ban-unban-user-key"
                        title={
                          anonUser.banned ? t("Unban user") : t("Ban user")
                        }
                        disabled={
                          ticketInfo.status === "RESOLVED" ||
                          (ticketInfo.assignedTo !== user.id &&
                            user.rank !== "admin" &&
                            ticketInfo.closed_by !== user.id)
                        }
                        user={anonUser}
                        ticket={ticketInfo}
                        getData={setTicketInformation}
                      />
                      {!ticketInfo?.clean && (
                        <SideActionButton
                          key="retain-attachments-key"
                          title={
                            ticketInfo?.retain_attachments
                              ? t("Remove protection")
                              : t("Retain attachments")
                          }
                          disabled={false}
                          content={
                            ticketInfo?.retain_attachments ? (
                              <FiShieldOff />
                            ) : (
                              <FiShield color="#16a34a" />
                            )
                          }
                          callback={() =>
                            document
                              .getElementById("ticket-retain-attachments-modal")
                              .showModal()
                          }
                        />
                      )}
                      {ticketInfo.status === "RESOLVED" ||
                      ticketInfo.status === "CLOSED" ? (
                        <SideActionButton
                          key="reopen-closed-ticket-key"
                          title={t("Reopen ticket")}
                          disabled={
                            !Object.keys(categories).includes(
                              ticketInfo.category,
                            )
                          }
                          content={<VscIssueReopened />}
                          callback={() => {
                            setIsByReply(false);
                            document
                              .getElementById("ticket-reopen-modal")
                              .showModal();
                          }}
                        />
                      ) : (
                        <SideActionButton
                          key="complete-ticket-fast-action-key"
                          title={t("Fast complete ticket")}
                          disabled={
                            ticketInfo.assignedTo !== user.id &&
                            user.rank !== "admin"
                          }
                          content={<MdDoneOutline />}
                          callback={() =>
                            document
                              .getElementById("ticket-fast-complete-modal")
                              .showModal()
                          }
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="divider before:h-[1px] after:h-[1px]">
              {t("Description")}
            </div>
            <div className="flex flex-col w-full gap-3 px-5">
              <span className="font-semibold flex items-center gap-2 text-lg">
                {t("Subject")}:{" "}
                <span className="font-normal">{ticketInfo.subject}</span>
              </span>
              <div className="w-full h-20 overflow-auto">
                <span>{ticketInfo.description}</span>
              </div>
              <ImageViewer imageUrls={ticketInfo.attachments} />
            </div>
          </div>

          <div className="flex flex-col bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-xl shadow-lg gap-3 py-5 px-4 h-fit">
            <div
              className="flex justify-between items-center cursor-pointer 2xl:cursor-default"
              onClick={() =>
                window.innerWidth < 1536 &&
                setShowMobileLastTickets(!showMobileLastTickets)
              }
            >
              <h2 className="text-lg font-bold border-b 2xl:border-none pb-2 2xl:pb-0 mb-2 2xl:mb-0 w-full 2xl:w-auto">
                {t("Last Tickets")}
              </h2>
              <div className="2xl:hidden text-2xl">
                {showMobileLastTickets ? <HiChevronUp /> : <HiChevronDown />}
              </div>
            </div>

            <div
              className={`flex flex-col gap-3 overflow-y-auto transition-all duration-300 ease-in-out ${showMobileLastTickets ? "max-h-[1000px] opacity-100 mt-2" : "max-h-0 2xl:max-h-[500px] opacity-0 2xl:opacity-100 overflow-hidden 2xl:overflow-y-auto"}`}
            >
              {lastTickets.length > 0 ? (
                lastTickets.map((ticket) => (
                  <NavLink
                    key={ticket.id}
                    className={`p-3 grid grid-cols-2 gap-4 justify-between border border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer transition-colors ${ticket.id === ticketInfo.id ? "bg-gray-100 dark:bg-gray-500" : ""}`}
                    to={`/admin/${ticket.fetch_code}/edit`}
                  >
                    <div className="flex flex-col justify-between">
                      <StatusBubble ticketStatus={ticket.status} />
                      <div>
                        <span className="flex gap-1">
                          <p className="text-sm font-semibold truncate">
                            {t("Subject")}:{" "}
                          </p>
                          <p className="text-sm font-medium truncate">
                            {ticket.subject}
                          </p>
                        </span>
                        <p className="text-xs opacity-70 truncate w-max-full">
                          {ticket.description}
                        </p>
                      </div>
                    </div>
                    <div className="justify-end flex flex-col items-end text-end gap-y-2">
                      <div className="flex flex-col gap-x-2">
                        <span className="text-sm">{t("Last Update")}:</span>
                        <span className="text-xs mt-1 opacity-60">
                          {formatTimestamp(ticket.lastUpdated * 1000)}
                        </span>
                      </div>
                      <div className="grid">
                        <span className="text-sm">
                          {t("Ticket assigned to")}:{" "}
                        </span>
                        {ticket.assignedTo ? (
                          <div className="flex items-center p-1 gap-2 border rounded-lg max-w-48 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600  text-sm px-2">
                            <div className="chat-image avatar">
                              <div className="w-8 rounded-full">
                                {devs[ticket.assignedTo].avatar.length < 3 ? (
                                  <div className="w-8 h-8 rounded-full bg-fuchsia-700 flex items-center justify-center text-white text-md select-none">
                                    {"GM"}
                                  </div>
                                ) : (
                                  <img
                                    alt="Tailwind CSS chat bubble component"
                                    src={setImageUrl(
                                      devs[ticket.assignedTo].avatar,
                                    )}
                                  />
                                )}
                              </div>
                            </div>
                            <h1 className="truncate">
                              {devs[ticket.assignedTo]?.name}
                            </h1>
                          </div>
                        ) : (
                          <span className="text-lg text-red-600 dark:text-red-500 font-semibold">
                            {t("Not Assigned")}
                          </span>
                        )}
                      </div>
                    </div>
                  </NavLink>
                ))
              ) : (
                <span className="text-sm italic opacity-50">
                  {t("Kayıt bulunamadı.")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full ">
          <div className="hidden">
            <a
              id="redirect-to-user-ticket"
              className="relative flex h-5 w-5"
              href={`/ticket/${ticket_info}`}
              target="_blank"
            >
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-sky-500 text-sky-100">
                <MdOutlineSentimentVeryDissatisfied size={20} />
              </span>
            </a>
          </div>
          <div className="flex flex-col 2xl:grid 2xl:grid-cols-[3fr_2fr] gap-5 py-1">
            <div className="flex flex-col w-full">
              <ChatViewer
                key={ticketInfo.chat_id}
                socket={socket}
                ticketInfo={ticketInfo}
                chatId={ticketInfo.chat_id}
                firstPerson={user}
                anonUser={anonUser}
                setAnonUser={setAnonUser}
                dev={true}
                setTicketInformation={setTicketInformation}
                isSendingMessage={isSendingMessage}
                replyTo={replyTo}
                setReplyTo={setReplyTo}
              />
              <MessageWriteComponent
                ticketInfo={ticketInfo}
                user={user}
                getData={getData}
                message={newMessage}
                completeInProgress={completeInProgress}
                handleSendMessage={handleSendMessage}
                handleSendAndComplete={handleSendAndComplete}
                replyTo={replyTo}
                setReplyTo={setReplyTo}
              />
            </div>

            <button
              onClick={() => setShowMobileNotes(!showMobileNotes)}
              className="fixed bottom-24 right-6 2xl:hidden z-[60] btn btn-circle bg-gray-900 text-gray-200 shadow-2xl scale-125"
            >
              <MdNoteAlt size={24} />
            </button>

            <div
              className={`fixed inset-0 bg-black/50 z-[200] 2xl:hidden transition-opacity duration-300 ${showMobileNotes ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
              onClick={() => setShowMobileNotes(false)}
            >
              <div
                className={`absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl p-4 transition-transform duration-300 ease-out h-[100vh] overflow-hidden ${showMobileNotes ? "translate-y-0" : "translate-y-full"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowMobileNotes(false)}
                  className="bg-red-600 hover:bg-red-500 p-2 text-md rounded-full absolute right-2 top-4 z-50"
                >
                  <IoCloseSharp size={24} />
                </button>
                <div className="w-full h-full pb-20">
                  <NoteViewer
                    key={`${ticketInfo.notes_id}-mobile`}
                    socket={socket}
                    notesId={ticketInfo.notes_id}
                    setNotesId={setNotesId}
                    ticketInfo={ticketInfo}
                    replyTo={replyTo}
                    setReplyTo={setReplyTo}
                  />
                </div>
              </div>
            </div>

            <div className="hidden xl:flex transition-all duration-300">
              <NoteViewer
                key={ticketInfo.notes_id}
                socket={socket}
                notesId={ticketInfo.notes_id}
                setNotesId={setNotesId}
                ticketInfo={ticketInfo}
                replyTo={replyTo}
                setReplyTo={setReplyTo}
              />
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default EditTicket;
